import { useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useGetProfileImagesMutation } from "../../../services/ActionReports/ProfileImageAPI";
import { addProfileImages, markProfileImagesFailed } from "../../../reducers/profileImages";
import type { ActionReportRow } from "../common/ActionReportListView/types";
import { getAffAboId } from "../common/ActionReportListView/utils/actionReportUtils";

export type ImageFetchState = "unfetched" | "fetching" | "fetched" | "failed";

const BATCH_SIZE = 25;

export const useLazyProfileImages = () => {
  const dispatch = useAppDispatch();
  const [fetchProfileImages] = useGetProfileImagesMutation();
  const fetchingRef = useRef<Set<string>>(new Set());

  const profileImagesState = useAppSelector((state) => state.profileImages);
  const images = profileImagesState?.images ?? {};
  const fetched = profileImagesState?.fetched ?? [];
  const failed = profileImagesState?.failed ?? [];

  const getImageFetchState = useCallback(
    (affAbo: string): ImageFetchState => {
      if (fetchingRef.current.has(affAbo)) return "fetching";
      if (failed.includes(affAbo)) return "failed";
      if (fetched.includes(affAbo)) return "fetched";
      return "unfetched";
    },
    [fetched, failed],
  );

  const getProfileImage = useCallback(
    (affAbo: string): string | undefined => {
      return images[affAbo];
    },
    [images],
  );

  const fetchImages = useCallback(
    async (rows: ActionReportRow[]) => {
      // Filter to only rows with imageRefUrl that haven't been fetched yet
      const rowsToFetch = rows.filter((row) => {
        if (!row.imageRefUrl) return false;
        const affAbo = getAffAboId(row);
        if (!affAbo) return false;
        return getImageFetchState(affAbo) === "unfetched";
      });

      if (rowsToFetch.length === 0) return;

      // Batch in groups of 25
      for (let i = 0; i < rowsToFetch.length; i += BATCH_SIZE) {
        const batch = rowsToFetch.slice(i, i + BATCH_SIZE);
        const requestAbos = batch.map((row) => ({
          affAbo: getAffAboId(row)!,
          imageRefUrl: row.imageRefUrl!,
        }));

        const affAbos = requestAbos.map((r) => r.affAbo);
        affAbos.forEach((id) => fetchingRef.current.add(id));

        try {
          const response = await fetchProfileImages({ abos: requestAbos }).unwrap();

          dispatch(
            addProfileImages({
              affAbos,
              images: response.images,
            }),
          );
        } catch {
          dispatch(markProfileImagesFailed({ affAbos }));
        } finally {
          affAbos.forEach((id) => fetchingRef.current.delete(id));
        }
      }
    },
    [getImageFetchState, dispatch, fetchProfileImages],
  );

  return { fetchImages, getProfileImage, getImageFetchState };
};
